import {
  addMonths,
  endOfMonth,
  format,
  isValid,
  parseISO,
  startOfMonth,
} from 'date-fns';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { TimelineRecord } from 'src/types/convocation-summary';
import { UQBAR_DATE_FORMAT, UQBAR_TIMEZONE } from 'src/utils/date';

import {
  TimelineCalendarWindow,
  TimelineDateRange,
  TimelineEventDate,
} from './calendarTypes';
import { getTimelineToday } from './timelineData';

dayjs.extend(utc);
dayjs.extend(timezone);

const CIVIL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const isCivilDate = (value?: string | null): value is string => {
  if (!value || !CIVIL_DATE_PATTERN.test(value)) return false;

  const date = parseISO(value);
  return isValid(date) && format(date, 'yyyy-MM-dd') === value;
};

export const toCivilDate = (date: Date) => format(date, 'yyyy-MM-dd');

/** Converte `YYYY-MM-DD` em Date local à meia-noite, sem deslocar o dia. */
export const fromCivilDate = (value: string) => parseISO(value);

/**
 * Normaliza datas da API para o dia civil: datas `YYYY-MM-DD` são mantidas e
 * timestamps são convertidos para o fuso da plataforma.
 */
export const toTimelineCivilDate = (value?: string | null) => {
  if (!value) return null;
  if (CIVIL_DATE_PATTERN.test(value)) return isCivilDate(value) ? value : null;

  const parsedDate = parseISO(value);
  if (!isValid(parsedDate)) return null;

  return dayjs(parsedDate).tz(UQBAR_TIMEZONE).format(UQBAR_DATE_FORMAT);
};

/** Data efetiva do evento: Referência e, na ausência dela, Entrega. */
export const getTimelineEventDate = (
  record: Pick<TimelineRecord, 'datareferencia' | 'dataentrega'>,
): TimelineEventDate | null => {
  const referenceDate = toTimelineCivilDate(record.datareferencia);
  if (referenceDate) return { date: referenceDate, kind: 'reference' };

  const deliveryDate = toTimelineCivilDate(record.dataentrega);
  if (deliveryDate) return { date: deliveryDate, kind: 'delivery' };

  return null;
};

export const isTimelineFutureEvent = (
  record: Pick<TimelineRecord, 'datareferencia' | 'dataentrega'>,
  today = getTimelineToday(),
) => {
  const eventDate = getTimelineEventDate(record);
  return Boolean(eventDate && eventDate.date > today);
};

const toUtcDay = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
};

export const getDaysUntil = (date: string, today = getTimelineToday()) =>
  Math.round((toUtcDay(date) - toUtcDay(today)) / DAY_IN_MS);

export const formatCountdown = (days: number) => {
  if (days < 0) return null;
  if (days === 0) return 'Hoje';
  if (days === 1) return 'Amanhã';
  return `Em ${days} dias`;
};

export const getInitialVisibleMonth = (dateStart: string, tomorrow: string) =>
  startOfMonth(
    fromCivilDate(
      isCivilDate(dateStart) && dateStart > tomorrow ? dateStart : tomorrow,
    ),
  );

export const getCalendarWindow = (
  visibleMonth: Date,
  monthsVisible: number,
  tomorrow: string,
): TimelineCalendarWindow => {
  const monthStart = toCivilDate(startOfMonth(visibleMonth));

  return {
    start: monthStart > tomorrow ? monthStart : tomorrow,
    end: toCivilDate(
      endOfMonth(addMonths(visibleMonth, Math.max(monthsVisible, 1) - 1)),
    ),
  };
};

/**
 * Primeiro clique define a âncora e já filtra o dia; o segundo fecha o
 * período em ordem crescente, inclusive entre meses.
 */
export const nextRangeSelection = (
  anchor: string | null,
  day: string,
): { anchor: string | null; range: TimelineDateRange } => {
  if (!anchor) {
    return { anchor: day, range: { dateStart: day, dateEnd: day } };
  }

  return {
    anchor: null,
    range:
      anchor <= day
        ? { dateStart: anchor, dateEnd: day }
        : { dateStart: day, dateEnd: anchor },
  };
};

export const getDayRangePosition = (
  day: string,
  { dateStart, dateEnd }: TimelineDateRange,
) => {
  const isRangeStart = Boolean(dateStart) && day === dateStart;
  const isRangeEnd = Boolean(dateEnd) && day === dateEnd;
  const isInRange =
    Boolean(dateStart && dateEnd) && day >= dateStart && day <= dateEnd;

  return {
    isRangeStart,
    isRangeEnd,
    isInRange: isInRange || isRangeStart || isRangeEnd,
  };
};
